// Constants for modal management
const LOCALIZATION_MODAL_ID = 'localization-modal';
const LOCALIZATION_MODAL_BACKDROP_SELECTOR = '.modal__backdrop';
const LOCALIZATION_MODAL_CLOSE_SELECTOR = '.modal__close';
const DATA_OPEN_LOCALIZATION_MODAL_SELECTOR = '[data-open-localization-modal]';
const MODAL_SWITCHERS_SELECTOR = '.modal__switchers';
const MODAL_CANCEL_SELECTOR = '.modal__cancel';
const MODAL_SELECT_LANG_SELECTOR = '.modal__select-lang';
const MODAL_BODY_SELECTOR = '.modal__body';
const LOCALIZATION_MODAL_COOKIE = 'localizationModal';

// Country and language selector
class LocalizationForm extends HTMLElement {
    constructor() {
        super();
        this.elements = {
            input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
            button: this.querySelector('button'),
            panel: this.querySelector('ul'),
        };
        this.handleButtonClick = this.handleButtonClick.bind(this);
        this.handleItemClick = this.handleItemClick.bind(this);
        this.elements.button.addEventListener('click', this.handleButtonClick);
        this.elements.button.addEventListener('touchstart', this.handleButtonClick);
        this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

        let isScrolling = false;
        let touchStartX = 0;
        let touchStartY = 0;
        let scrollTimeout;

        const SCROLL_THRESHOLD = 10; // Adjust the threshold as needed

        window.addEventListener('scroll', () => {
            isScrolling = true;
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                isScrolling = false;
            }, 100); // Adjust the timeout as needed
        });

        this.querySelectorAll('a').forEach(item => {
            item.addEventListener('touchstart', event => {
                touchStartX = event.touches[0].clientX;
                touchStartY = event.touches[0].clientY;
            });

            item.addEventListener('touchend', event => {
                const touchEndX = event.changedTouches[0].clientX;
                const touchEndY = event.changedTouches[0].clientY;
                const distanceX = Math.abs(touchEndX - touchStartX);
                const distanceY = Math.abs(touchEndY - touchStartY);

                if (distanceX < SCROLL_THRESHOLD && distanceY < SCROLL_THRESHOLD && !isScrolling) {
                    this.handleItemClick(event);
                } else {
                    event.preventDefault();
                }
            });

            item.addEventListener('click', event => {
                if (isScrolling) {
                    event.preventDefault();
                    return;
                }
                this.handleItemClick(event);
            });
        });
    }

    hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
    }

    onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;

        this.hidePanel();
        this.elements.button.focus();
    }

    handleItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
        this.hidePanel();
    }

    handleButtonClick(event) {
        event.preventDefault();
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        const isExpanded = this.elements.button.getAttribute('aria-expanded') === 'true';
        this.elements.button.setAttribute('aria-expanded', (!isExpanded).toString());
    }

    closeSelector(event) {
        const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
        if (event.relatedTarget === null || shouldClose) {
            this.hidePanel();
        }
    }
}

customElements.define('localization-form', LocalizationForm);

class CookieManager {
    // Function to set a cookie
    static setCookie(name, value, days) {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = "; expires=" + date.toUTCString();
        }
        document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value || "")}${expires}; path=/; SameSite=Lax`;
    }

    // Function to get a cookie
    static getCookie(name) {
        const nameEQ = `${encodeURIComponent(name)}=`;
        const ca = document.cookie.split(';');
        for (let c of ca) {
            c = c.trim();
            if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
        }
        return null;
    }

    // Function to delete a cookie
    static deleteCookie(name) {
        document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    }
}

// Function to toggle modal elements
const selectLangSpan = document.querySelector(MODAL_SELECT_LANG_SELECTOR);
const cancelSpan = document.querySelector(MODAL_CANCEL_SELECTOR);

const toggleModalElements = () => {
    const modalSwitchers = document.querySelector(MODAL_SWITCHERS_SELECTOR);
    const modalBody = document.querySelector(MODAL_BODY_SELECTOR);
    // Toggle hidden attribute for modalSwitchers, cancelSpan, and selectLangSpan
    modalSwitchers.toggleAttribute('hidden');
    if (cancelSpan) cancelSpan.toggleAttribute('hidden');
    if (selectLangSpan) selectLangSpan.toggleAttribute('hidden');
    // Toggle hidden attribute for modalBody based on its current state
    modalBody.toggleAttribute('hidden');
};

// Event listeners for modal element toggling
if (selectLangSpan) {
    selectLangSpan.addEventListener('click', toggleModalElements);
    selectLangSpan.addEventListener('touchstart', toggleModalElements);
}
if (cancelSpan) {
    cancelSpan.addEventListener('click', toggleModalElements);
    cancelSpan.addEventListener('touchstart', toggleModalElements);
}

// Modal focus trap for WCAG compliance
class LocalizationModal {
    static FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])';
    constructor(modalId = LOCALIZATION_MODAL_ID, backdropSelector = LOCALIZATION_MODAL_BACKDROP_SELECTOR) {
        this.modal = document.getElementById(modalId);
        this.backdrop = document.querySelector(backdropSelector);
        this.lastFocusedElement = null;
        this.trapHandler = this.trapFocus.bind(this);
        // Manual open/close triggers
        document.querySelectorAll(DATA_OPEN_LOCALIZATION_MODAL_SELECTOR).forEach(btn =>
            btn.addEventListener('click', (e) => this.open(e))
        );
        document.querySelectorAll(`#${modalId} ${LOCALIZATION_MODAL_CLOSE_SELECTOR}`).forEach(btn =>
            btn.addEventListener('click', () => this.close())
        );
    }

    open(event) {
        this.lastFocusedElement = event ? event.currentTarget : document.activeElement;
        this.modal.hidden = false;
        if (this.backdrop) this.backdrop.hidden = false;

        // Focus the close button for accessibility
        const closeBtn = this.modal.querySelector('.modal__close');
        if (closeBtn) closeBtn.focus();

        this.modal.addEventListener('keydown', this.trapHandler);
        // Add ESC key handler
        this.escHandler = (e) => {
            if (e.key === 'Escape' || e.key === 'Esc') {
                e.preventDefault();
                this.close();
            }
        };
        document.addEventListener('keydown', this.escHandler);
    }

    close() {
        this.modal.hidden = true;
        if (this.backdrop) this.backdrop.hidden = true;
        this.modal.removeEventListener('keydown', this.trapHandler);
        // Remove ESC key handler
        if (this.escHandler) {
            document.removeEventListener('keydown', this.escHandler);
            this.escHandler = null;
        }
        // Restore focus to the last focused element
        if (this.lastFocusedElement) this.lastFocusedElement.focus();

        // Set cookie to prevent showing modal again
        CookieManager.setCookie(LOCALIZATION_MODAL_COOKIE, 'true', 14);
    }

    trapFocus(e) {
        if (e.key !== 'Tab') return;
        const focusables = this.modal.querySelectorAll(LocalizationModal.FOCUSABLE_SELECTOR);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        }
    }

    autoOpenIfNeeded(countryName) {
        if (!this.modal || !this.backdrop) return;
        if (countryName === 'United States' || CookieManager.getCookie(LOCALIZATION_MODAL_COOKIE) === 'true') {
            this.modal.hidden = true;
            this.backdrop.hidden = true;
        } else {
            setTimeout(() => {
                this.open();
                CookieManager.setCookie(LOCALIZATION_MODAL_COOKIE, 'true', 14);
            }, 2000);
        }
    }
}

// Initialize the LocalizationModal on DOMContentLoaded
document.addEventListener('DOMContentLoaded', async () => {
    let countryName = '';
    try {
        const localizationData = await themeObject.fetchLocalization();
        if (localizationData && localizationData.detected_values && localizationData.detected_values.country_name) {
            countryName = localizationData.detected_values.country_name;
        }
    } catch (error) {
        console.error('Error fetching localization data:', error);
    }
    const localizationModal = new LocalizationModal();
    localizationModal.autoOpenIfNeeded(countryName);
});
